import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { ReaAuditPresenter } from '../../presenters/rea-audit.presenter';
import { ReaSupportEmailPresenter } from '../../presenters/rea-support-email.presenter';

import {
  ReaImportAuditDetail,
  ReaImportAuditDetailResponse,
  ReaImportAuditRecord,
  ReaRecoveryAction,
  ReaTechnicalDetailItem,
} from 'src/app/models/fincas/rea.model';

/**
 * Shows previous REA imports and keeps support-only information in a secondary area.
 */
@Component({
  selector: 'app-rea-import-history',
  templateUrl: './rea-import-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReaImportHistoryComponent {
  @Input()
  public showPanel = false;

  @Input()
  public records: ReaImportAuditRecord[] = [];

  @Input()
  public selectedAuditImportId = '';

  @Input()
  public auditDetailLoading = false;

  @Input()
  public selectedAuditDetail: ReaImportAuditDetailResponse | null = null;

  @Input()
  public selectedAuditChanges: ReaImportAuditDetail[] = [];

  @Output()
  public readonly togglePanel = new EventEmitter<void>();

  @Output()
  public readonly selectRecord = new EventEmitter<ReaImportAuditRecord>();

  @Output()
  public readonly closeDetail = new EventEmitter<void>();

  public isSelectedAuditRecord(record: ReaImportAuditRecord): boolean {
    return record.importId !== '' && record.importId === this.selectedAuditImportId;
  }

  public isAuditRecordLoading(record: ReaImportAuditRecord): boolean {
    return this.isSelectedAuditRecord(record) && this.auditDetailLoading;
  }

  public auditSeverity(statusCode: number): 'success' | 'warn' | 'danger' | 'info' {
    return ReaAuditPresenter.severity(statusCode);
  }

  public auditStatusLabel(record: ReaImportAuditRecord): string {
    return ReaAuditPresenter.statusLabel(record);
  }

  public auditRecordTitle(record: ReaImportAuditRecord): string {
    return ReaAuditPresenter.title(record);
  }

  public auditRecordDescription(record: ReaImportAuditRecord): string {
    return ReaAuditPresenter.description(record);
  }

  public auditRecordNextStep(record: ReaImportAuditRecord): string {
    return ReaAuditPresenter.nextStep(record);
  }

  public auditRecordRecoveryAction(record: ReaImportAuditRecord): ReaRecoveryAction | null {
    return ReaAuditPresenter.recoveryAction(record);
  }

  public auditRecordTechnicalDetails(record: ReaImportAuditRecord): ReaTechnicalDetailItem[] {
    return ReaAuditPresenter.technicalDetails(record);
  }

  public isAuditError(record: ReaImportAuditRecord): boolean {
    return ReaAuditPresenter.isError(record);
  }

  public supportEmailHrefForAuditRecord(record: ReaImportAuditRecord): string {
    return ReaSupportEmailPresenter.auditRecordHref({
      importId: record.importId,
      createdAt: record.createdAt,
      nif: record.nif,
      codigoRea: record.codigoRea,
      statusCode: record.statusCode,
      title: this.auditRecordTitle(record),
      description: this.auditRecordDescription(record),
      nextStep: this.auditRecordNextStep(record),
      technicalDetails: this.auditRecordTechnicalDetails(record),
    });
  }

  public auditChangeTechnicalDetails(change: ReaImportAuditDetail): ReaTechnicalDetailItem[] {
    const details: ReaTechnicalDetailItem[] = [];

    this.appendTechnicalDetail(details, 'Entidad', change.entity);
    this.appendTechnicalDetail(details, 'Código', change.code);
    this.appendTechnicalDetail(details, 'Acción', this.actionLabel(change.action));
    this.appendTechnicalDetail(details, 'Antes', this.stringifyJson(change.before));
    this.appendTechnicalDetail(details, 'Después', this.stringifyJson(change.after));

    return details;
  }

  public actionLabel(action: string): string {
    const labels: Record<string, string> = {
      inserted: 'Nueva',
      updated: 'Actualizada',
      unchanged: 'Sin cambios',
      skipped: 'Omitida',
      not_in_rea: 'No aparece en REA',
    };

    return labels[action] ?? action;
  }

  private appendTechnicalDetail(items: ReaTechnicalDetailItem[], label: string, value: string | null | undefined): void {
    if (value == null || value.trim() === '') {
      return;
    }

    items.push({ label, value: value.trim() });
  }

  private stringifyJson(value: unknown): string {
    if (value == null) {
      return '';
    }

    return JSON.stringify(value);
  }
}
